import React, { useState, useEffect } from "react";
import { AiOutlineDown, AiOutlineClose } from "react-icons/ai";
import { getPrograms, createOrder, verifyPayment } from "../api/api";
import { getToken } from "../api/storageService";

const ServicePremium = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFeaturesId, setOpenFeaturesId] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingProgramId, setProcessingProgramId] = useState(null);

  // Custom Toaster Function
  const showToast = (message, type = 'success') => {
    const existingToast = document.querySelector('.custom-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = `custom-toast toast-${type}`;
    
    const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';
    
    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
          ${icon}
        </div>
        <div style="flex: 1;">${message}</div>
        <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
      </div>
    `;
    
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: ${bgColor};
      color: white;
      padding: 14px 20px;
      border-radius: 12px;
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
      min-width: 300px;
      max-width: 400px;
      cursor: pointer;
      animation: slideInRight 0.3s ease;
      z-index: 10000;
    `;
    
    if (!document.querySelector('#toast-styles')) {
      const style = document.createElement('style');
      style.id = 'toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    }, 3000);
    
    toast.onclick = () => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    };
  };

  // Load Razorpay script dynamically
  useEffect(() => {
    const loadRazorpayScript = () => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        showToast('Failed to load payment gateway', 'error');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  // Fetch programs from API
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        const result = await getPrograms();
        
        if (result.status && result.data) {
          setPrograms(result.data);
          
          // Initialize selected variants with first variant of each program
          const initialVariants = {};
          result.data.forEach(program => {
            if (program.ProgramVariants && program.ProgramVariants.length > 0) {
              initialVariants[program.id] = program.ProgramVariants[0];
            }
          });
          setSelectedVariants(initialVariants);
        } else {
          setError(result.message || "Failed to fetch programs");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch programs");
        console.error("Error fetching programs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  const toggleFeatures = (programId) => {
    setOpenFeaturesId(openFeaturesId === programId ? null : programId);
  };

  const handleVariantChange = (programId, variant) => {
    setSelectedVariants(prev => ({
      ...prev,
      [programId]: variant
    }));
  };

const initializeRazorpayPayment = (razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId) => {
  if (window.Razorpay) {
    openRazorpay(razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId);
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => openRazorpay(razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId);
  script.onerror = () => {
    showToast('Failed to load payment gateway. Please refresh the page.', 'error');
    setProcessingPayment(false);
    setProcessingProgramId(null);
  };
  document.body.appendChild(script);
};

const openRazorpay = (razorpayOrderId, razorpayKeyId, amountInPaise, program, variant, purchaseId) => {
  // amountInPaise is already in paise (e.g., 590000 = ₹5900)
  // No need to multiply by 100 again
  
  // Get user details from localStorage if available
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  
  const options = {
    key: razorpayKeyId,  // This is the key from API response
    amount: amountInPaise,  // Amount is already in paise
    currency: 'INR',
    name: 'Nutrillion',
    description: `${program.name} - ${variant.duration}`,
    order_id: razorpayOrderId,  // This is the order_id from API response
    prefill: {
      name: userData?.fullName || userData?.name || '',
      email: userData?.email || '',
      contact: userData?.phoneNumber || userData?.phone || ''
    },
    theme: {
      color: '#F59E0B'
    },
    modal: {
      ondismiss: () => {
        showToast('Payment cancelled', 'info');
        setProcessingPayment(false);
        setProcessingProgramId(null);
      }
    },
    handler: async (response) => {
      await verifyProgramPayment(response, razorpayOrderId, purchaseId);
    }
  };

  console.log('Razorpay Options:', {
    key: razorpayKeyId,
    amount: amountInPaise,
    order_id: razorpayOrderId
  });

  try {
    const razorpay = new window.Razorpay(options);
    
    razorpay.on('payment.failed', (response) => {
      console.error('Payment failed:', response);
      showToast(`Payment failed: ${response.error?.description || 'Please try again'}`, 'error');
      setProcessingPayment(false);
      setProcessingProgramId(null);
    });
    
    razorpay.open();
  } catch (error) {
    console.error('Razorpay initialization error:', error);
    showToast('Failed to initialize payment. Please try again.', 'error');
    setProcessingPayment(false);
    setProcessingProgramId(null);
  }
};

  const verifyProgramPayment = async (paymentResponse, razorpayOrderId, purchaseId) => {
    showToast('Verifying payment...', 'info');
    
    try {
      const verificationData = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        purchase_id: purchaseId
      };
      
      const verificationResult = await verifyPayment(verificationData);
      
      if (verificationResult?.status === true || verificationResult?.success === true) {
        showToast('Payment successful! Your purchase is confirmed.', 'success');
        // Optionally redirect to my purchases page
        setTimeout(() => {
          window.location.href = '/my-purchases';
        }, 2000);
      } else {
        showToast(verificationResult?.message || 'Payment verification failed. Please contact support.', 'error');
      }
      
    } catch (error) {
      console.error('Payment verification error:', error);
      showToast('Payment verification failed. Please contact support.', 'error');
    } finally {
      setProcessingPayment(false);
      setProcessingProgramId(null);
    }
  };

const handlePurchase = async (program, variant) => {
  // Check if token exists
  const token = getToken();
  
  if (!token) {
    showToast('Please login to continue with purchase', 'error');
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
    return;
  }

  setProcessingPayment(true);
  setProcessingProgramId(program.id);

  try {
    const orderData = {
      program_id: program.id,
      variant_id: variant.id,
      payment_method: "razorpay",
      auto_renew: false
    };
    
    const orderResponse = await createOrder(orderData);
    console.log('Order response:', orderResponse);
    
    // Fix: Check the response structure
    if (orderResponse?.status === true && orderResponse?.data) {
      const apiData = orderResponse.data;
      
      // Fix: Use correct field names from API response
      const razorpayOrderId = apiData.order_id;  // Changed from razorpay_order_id
      const razorpayKeyId = apiData.razorpay_key;  // Changed from razorpay_key_id
      const amountFromApi = apiData.amount; // Amount is already in paise (590000 = ₹5900)
      const purchaseId = apiData.purchase_id;
      
      console.log('Razorpay Order ID:', razorpayOrderId);
      console.log('Razorpay Key:', razorpayKeyId);
      console.log('Amount in paise:', amountFromApi);
      
      if (razorpayOrderId && razorpayKeyId) {
        // Convert paise to rupees for display (if needed)
        const amountInRupees = amountFromApi / 100;
        
        initializeRazorpayPayment(
          razorpayOrderId,
          razorpayKeyId,
          amountFromApi, // Pass amount in paise directly
          program,
          variant,
          purchaseId
        );
      } else {
        console.error('Missing Razorpay order details:', apiData);
        showToast('Failed to create payment order. Please try again.', 'error');
        setProcessingPayment(false);
        setProcessingProgramId(null);
      }
    } else {
      console.error('API returned unsuccessful response:', orderResponse);
      showToast(orderResponse?.message || 'Failed to create order. Please try again.', 'error');
      setProcessingPayment(false);
      setProcessingProgramId(null);
    }
    
  } catch (error) {
    console.error("Purchase error details:", error);
    
    if (error.response) {
      console.error('Error response:', error.response.data);
      
      if (error.response.data?.errors) {
        const validationError = error.response.data.errors[0];
        showToast(validationError.msg || validationError.message, 'error');
      } else if (error.response.data?.message) {
        showToast(error.response.data.message, 'error');
      } else if (error.response.status === 401) {
        showToast('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else if (error.response.status === 500) {
        showToast('Server error. Please try again later.', 'error');
      } else {
        showToast('Failed to create order. Please try again.', 'error');
      }
    } else if (error.request) {
      showToast('Network error. Please check your connection.', 'error');
    } else {
      showToast(error.message || 'An unexpected error occurred.', 'error');
    }
    
    setProcessingPayment(false);
    setProcessingProgramId(null);
  }
};

  // Format price with currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(price));
  };

  // Calculate price with GST
  const getPriceWithGST = (price, gstPercentage) => {
    const basePrice = parseFloat(price);
    if (!gstPercentage || gstPercentage === 0) return basePrice;
    const gstAmount = (basePrice * parseFloat(gstPercentage)) / 100;
    return basePrice + gstAmount;
  };

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return imagePath;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ {error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-yellow-600 text-white px-6 py-2 rounded-full hover:bg-yellow-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="pt-80 pb-80 bg-white custom-padding xl-screen-padding">
        <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6 client-text pb-12">
          Our Premium <span className="text-yellow-600">Health Programs</span>
        </h2>

        {programs.map((program, index) => (
          <div key={program.id} className={index > 0 ? "pt-80" : ""}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className={`md:col-span-4 relative rounded-lg overflow-hidden ${index % 2 === 1 ? 'md:order-last' : ''}`}>
                {getImageUrl(program.image) ? (
                  <img
                    src={getImageUrl(program.image)}
                    alt={program.name}
                    className="w-full h-64 md:h-80 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-200 flex items-center justify-center rounded-lg">
                    <span className="text-gray-400">No Image Available</span>
                  </div>
                )}
              </div>

              <div className="md:col-span-8">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 client-text">
                  {index + 1}. {program.name}
                </h3>
                <p className="text-[#939a9e] text-p mt-4 mb-6 leading-relaxed">
                  {program.description}
                </p>
                
                {/* Duration Buttons */}
                {program.ProgramVariants && program.ProgramVariants.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-4 mb-4">
                    {program.ProgramVariants.map((variant) => (
                      <button
                        key={variant.id}
                        onClick={() => handleVariantChange(program.id, variant)}
                        className={`px-4 py-1 rounded-full font-semibold transition-all duration-300 ${
                          selectedVariants[program.id]?.id === variant.id
                            ? "bg-yellow-600 text-white shadow-md"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        {variant.duration}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Price Display */}
                {selectedVariants[program.id] && (
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-yellow-600">
                      {formatPrice(getPriceWithGST(
                        selectedVariants[program.id].price,
                        selectedVariants[program.id].gst_percentage
                      ))}
                    </span>
                    <span className="text-gray-600 ml-2">INR</span>
                    {selectedVariants[program.id].gst_percentage && 
                     selectedVariants[program.id].gst_percentage > 0 && (
                      <span className="text-sm text-gray-500 ml-2">
                        (Incl. GST {selectedVariants[program.id].gst_percentage}%)
                      </span>
                    )}
                  </div>
                )}
                <button 
                  className="bg-button-services common-btn-all text-white font-lato text-[10px] sm:text-sm md:text-[12px] px-3 py-1 sm:px-6 sm:py-2 md:px-6 md:py-3 rounded-full w-[120px] sm:w-auto md:w-[200px] lg:w-[120px] whitespace-nowrap" 
                  onClick={() => handlePurchase(program, selectedVariants[program.id])}
                  disabled={processingPayment && processingProgramId === program.id}
                >
                  {processingPayment && processingProgramId === program.id ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                      Processing...
                    </>
                  ) : (
                    "Purchase Now"
                  )}
                </button>
              </div>
            </div>

            {/* Key Features Accordion */}
            {program.ProgramFeatures && program.ProgramFeatures.length > 0 && (
              <div className="w-full mx-auto pt-80">
                <div className="w-full bg-[#f2f0ed] p-2 rounded-[30px] shadow-md">
                  <button
                    className="w-full flex justify-between client-text items-center bg-transparent px-6 py-4 rounded-[30px] text-xl font-semibold hover:bg-[#e8e6e2] transition"
                    onClick={() => toggleFeatures(program.id)}
                  >
                    <span>Key Features</span>
                    {openFeaturesId === program.id ? 
                      <AiOutlineClose className="w-5 h-5" /> : 
                      <AiOutlineDown className="w-5 h-5" />
                    }
                  </button>

                  {openFeaturesId === program.id && (
                    <div className="mt-0 pb-6">
                      <ul className="text-gray-700 space-y-4 p-6 text-p font-lato">
                        {program.ProgramFeatures.map((feature, idx) => (
                          <li key={feature.id || idx} className="flex items-start gap-3">
                            <span className="text-yellow-600 text-lg mt-0.5">✓</span>
                            <span className="flex-1 text-gray-700 leading-relaxed">
                              {feature.feature_text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </section>
    </>
  );
};

export default ServicePremium;